// =============================================================================
// LocationContext — global header switcher, persisted in localStorage
// =============================================================================
// Single source of truth for "which location is the operator working at right
// now?". Used by /calendar, /today, /sales, /payments. Default "All" so a
// fresh operator sees every venue; their first explicit pick sticks until
// they change it again.
//
// Locations come from fn_admin_walkin_refdata (already used by the new-
// booking modal). One round trip per session.
// =============================================================================
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { fetchWalkinRefData, type BookingLocation } from './walkin-data';

const STORAGE_KEY = 'neoseum-admin-location-id';

interface LocationContextValue {
  locationId: string | null;       // null = All
  setLocationId: (id: string | null) => void;
  locations: BookingLocation[];
  loading: boolean;
  error: string | null;
}

const Ctx = createContext<LocationContextValue | undefined>(undefined);

export function LocationProvider({ children }: { children: ReactNode }) {
  const [locations, setLocations] = useState<BookingLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationId, setLocationIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ref = await fetchWalkinRefData();
        if (cancelled) return;
        setLocations(ref.locations);
        // If the saved selection no longer corresponds to a bookable location
        // (e.g. someone disabled it), drop the stale value.
        if (
          locationId &&
          !ref.locations.some((l) => l.id === locationId)
        ) {
          setLocationIdState(null);
          try {
            window.localStorage.removeItem(STORAGE_KEY);
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only fetch on mount; locationId-dependent cleanup runs once after fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocationId = (id: string | null) => {
    setLocationIdState(id);
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore quota errors */
    }
  };

  const value = useMemo<LocationContextValue>(
    () => ({ locationId, setLocationId, locations, loading, error }),
    [locationId, locations, loading, error],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocationFilter(): LocationContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useLocationFilter must be used inside <LocationProvider>');
  }
  return v;
}
